/** Memoize a deterministic text projection, bounding both entries and retained
 * string code units. Oversized inputs are computed without displacing the
 * useful working set. FIFO eviction matches the note display caches. */
export function memoizeString<Result extends string | boolean>(
  compute: (input: string) => Result,
  { maxEntries = 2_000, maxCodeUnits = 4_000_000 } = {},
): (input: string) => Result {
  const cache = new Map<string, { value: Result; size: number }>();
  let codeUnits = 0;

  return (input) => {
    const cached = cache.get(input);

    if (cached !== undefined) {
      return cached.value;
    }

    const value = compute(input);
    const size = input.length + (typeof value === "string" ? value.length : 0);

    if (maxEntries <= 0 || size > maxCodeUnits) {
      return value;
    }

    while (cache.size >= maxEntries || codeUnits + size > maxCodeUnits) {
      const oldest = cache.entries().next().value;

      if (oldest === undefined) {
        break;
      }

      cache.delete(oldest[0]);
      codeUnits -= oldest[1].size;
    }

    cache.set(input, { value, size });
    codeUnits += size;
    return value;
  };
}
